import React, { useEffect, useState, useRef } from "react";
import { Dialog, TextField } from "orion-components/CBComponents";
import { feedService } from "client-app-core";
import { useMediaQuery, Button } from "@mui/material";
import { useTheme } from "@mui/styles";
import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import { dataReceived } from "../../../GlobalData/Actions";

const EntityDialog = props => {
	const { closeDialog, open, selectedEntity, dir } = props;
	const properties = selectedEntity?.entityData?.properties ?? {};

	const [overrideName, setOverrideName] = useState("");
	const [overrideDescription, setOverrideDescription] = useState("");

	const dispatch = useDispatch();

	const theme = useTheme();
	const isXS = useMediaQuery(theme.breakpoints.only("xs"));

	const handleChange = setState => event => {
		const value = event && event.target && !isNil(event.target.value) ? event.target.value : event;

		setState(value);
	};

	const handleSave = () => {
		const update = {
			overrideName,
			overrideDescription
		};

		feedService.updateEntity(selectedEntity.id, selectedEntity.entityType, update, (err, response) => {
			if (err) {
				console.error("Error updating entity:", err);
				return;
			}
			if (response.changes) {
				const data = response.changes[0]?.new_val;
				if (data) {
					dispatch(dataReceived(data, selectedEntity.feedId, "globalGeo"));
					dispatch(dataReceived(data, selectedEntity.feedId, "globalData"));
				}
			}
		});

		handleClose();
	};

	const handleClose = () => {
		dispatch(closeDialog("entityEditDialog"));
		setOverrideName("");
		setOverrideDescription("");
	};

	const styles = {
		pickers: {
			display: "flex",
			justifyContent: "space-between"
		},
		spacer: {
			padding: 16
		}
	};

	const usePrevious = value => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		}, [value]);
		return ref.current;
	};

	const prevProps = usePrevious(props);

	useEffect(() => {
		if (prevProps && !prevProps.open && open && selectedEntity) {
			const { name = "", overrideName, description = "", overrideDescription } = properties;
			setOverrideName(!isEmpty(overrideName) ? overrideName : name);
			setOverrideDescription(!isEmpty(overrideDescription) ? overrideDescription : description);
		}
	}, [props]);

	const checkDisabled = () => {
		const trackName = properties?.overrideName || properties?.name || "";
		const trackDescription = properties?.overrideDescription || properties?.description || "";

		return overrideName === trackName && overrideDescription === trackDescription;
	};

	const dialogActions = () => {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					padding: "4%",
					flexDirection: "row-reverse"
				}}
			>
				<div>
					<Button style={{ color: "#B2B6BB" }} variant="text" onClick={handleClose} key="close-action-button" data-testid="entity-edit-cancel-button">
						{getTranslation("global.profiles.entityProfile.entityDialog.cancel")}
					</Button>
					<Button
						data-testid="entity-edit-save-button"
						color="primary"
						variant="text"
						onClick={() => handleSave()}
						key="save-action-button"
						disabled={checkDisabled()}
					>
						{getTranslation("global.profiles.entityProfile.entityDialog.confirm")}
					</Button>
				</div>
			</div>
		);
	};

	return (
		<Dialog
			data-testid="entity-edit-dialog"
			title={getTranslation("global.profiles.entityProfile.entityDialog.title")}
			open={open}
			dir={dir}
			dialogContentStyles={{ padding: "20px 24px 8px 24px" }}
			actions={true}
			DialogActionsFunction={dialogActions}
		>
			<div style={{ width: isXS ? "auto" : 500 }}>
				<TextField
					data-testid="entity-name-input"
					id="overrideName"
					key="overrideName"
					label={getTranslation("global.profiles.entityProfile.entityDialog.trackName")}
					value={overrideName}
					handleChange={handleChange(setOverrideName)}
					fullWidth={true}
					autoFocus={true}
					dir={dir}
					inputLabelStyle={{
						fontSize: 14,
						color: "rgb(181, 185, 190)"
					}}
				/>
				<TextField
					data-testid="entity-description-input"
					id="overrideDescription"
					key="overrideDescription"
					label={getTranslation("global.profiles.entityProfile.entityDialog.description")}
					value={overrideDescription}
					handleChange={handleChange(setOverrideDescription)}
					fullWidth={true}
					dir={dir}
					inputLabelStyle={{
						fontSize: 14,
						color: "rgb(181, 185, 190)"
					}}
				/>
			</div>
		</Dialog>
	);
};

EntityDialog.propTypes = {
	closeDialog: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	selectedEntity: PropTypes.object.isRequired,
	dir: PropTypes.string.isRequired
};

export default EntityDialog;
